import { useState } from "react"

import { useTranslation } from "react-i18next"

import type { AlertConfig } from "@/api/alerts-api"

import { Bug, Mail, Save, Webhook } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Textarea } from "@/components/ui/textarea"

import { useUpdateAlertConfigMutation } from "../../hooks/admin-queries"

type Props = {
  config: AlertConfig
}

export function AlertsChannelsConfig({ config }: Props) {
  const { t } = useTranslation()
  const updateMutation = useUpdateAlertConfigMutation()

  // Email channel state
  const [emailEnabled, setEmailEnabled] = useState(config.channels.email.enabled)
  const [senderEmail, setSenderEmail] = useState(config.channels.email.sender_email || "")
  const [recipients, setRecipients] = useState((config.channels.email.recipients || []).join("\n"))

  // Error report recipient state
  const [errorReportRecipient, setErrorReportRecipient] = useState(
    config.error_report_recipient || ""
  )

  // Webhook channel state
  const [webhookEnabled, setWebhookEnabled] = useState(config.channels.webhook.enabled)
  const [webhookUrl, setWebhookUrl] = useState(config.channels.webhook.url || "")
  const [webhookHeaders, setWebhookHeaders] = useState(
    JSON.stringify(config.channels.webhook.headers || {}, null, 2)
  )

  const handleSaveEmail = async () => {
    const recipientList = recipients
      .split("\n")
      .map((r) => r.trim())
      .filter(Boolean)

    await updateMutation.mutateAsync({
      channels: {
        ...config.channels,
        email: {
          ...config.channels.email,
          enabled: emailEnabled,
          sender_email: senderEmail,
          recipients: recipientList,
        },
      },
    })
  }

  const handleSaveWebhook = async () => {
    let headers: Record<string, string> = {}
    try {
      headers = JSON.parse(webhookHeaders)
    } catch {
      // Invalid JSON, keep empty
    }

    await updateMutation.mutateAsync({
      channels: {
        ...config.channels,
        webhook: {
          ...config.channels.webhook,
          enabled: webhookEnabled,
          url: webhookUrl,
          headers,
        },
      },
    })
  }

  const handleSaveErrorReportRecipient = async () => {
    await updateMutation.mutateAsync({
      error_report_recipient: errorReportRecipient.trim(),
    })
  }

  return (
    <div className="grid gap-6 md:grid-cols-2">
      {/* Email Channel */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Mail className="size-5" />
              <CardTitle className="text-lg">
                {t("adminDashboard.alerts.emailChannel", "Email Channel")}
              </CardTitle>
            </div>
            <Badge variant={emailEnabled ? "default" : "secondary"}>
              {emailEnabled
                ? t("adminDashboard.alerts.enabled", "Enabled")
                : t("adminDashboard.alerts.disabled", "Disabled")}
            </Badge>
          </div>
          <CardDescription>
            {t(
              "adminDashboard.alerts.emailChannelDesc",
              "Send alerts via Microsoft Graph API email"
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <Switch
              checked={emailEnabled}
              id="alerts-email-enabled"
              onCheckedChange={setEmailEnabled}
            />
            <Label htmlFor="alerts-email-enabled">
              {t("adminDashboard.alerts.enableChannel", "Enable this channel")}
            </Label>
          </div>

          <div className="space-y-2">
            <Label htmlFor="senderEmail">
              {t("adminDashboard.alerts.senderEmail", "Sender Email")}
            </Label>
            <Input
              id="senderEmail"
              placeholder="alerts@company.com"
              value={senderEmail}
              onChange={(e) => setSenderEmail(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="recipients">
              {t("adminDashboard.alerts.recipients", "Recipients (one per line)")}
            </Label>
            <Textarea
              id="recipients"
              placeholder="admin@company.com&#10;ops@company.com"
              rows={4}
              value={recipients}
              onChange={(e) => setRecipients(e.target.value)}
            />
          </div>

          <Button className="w-full" disabled={updateMutation.isPending} onClick={handleSaveEmail}>
            <Save className="mr-2 size-4" />
            {t("common.save", "Save")}
          </Button>
        </CardContent>
      </Card>

      {/* Webhook Channel */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Webhook className="size-5" />
              <CardTitle className="text-lg">
                {t("adminDashboard.alerts.webhookChannel", "Webhook Channel")}
              </CardTitle>
            </div>
            <Badge variant={webhookEnabled ? "default" : "secondary"}>
              {webhookEnabled
                ? t("adminDashboard.alerts.enabled", "Enabled")
                : t("adminDashboard.alerts.disabled", "Disabled")}
            </Badge>
          </div>
          <CardDescription>
            {t("adminDashboard.alerts.webhookChannelDesc", "Send alerts to a custom HTTP endpoint")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <Switch
              checked={webhookEnabled}
              id="alerts-webhook-enabled"
              onCheckedChange={setWebhookEnabled}
            />
            <Label htmlFor="alerts-webhook-enabled">
              {t("adminDashboard.alerts.enableChannel", "Enable this channel")}
            </Label>
          </div>

          <div className="space-y-2">
            <Label htmlFor="webhookUrl">
              {t("adminDashboard.alerts.webhookUrl", "Webhook URL")}
            </Label>
            <Input
              id="webhookUrl"
              placeholder="https://hooks.example.com/alerts"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="webhookHeaders">
              {t("adminDashboard.alerts.headers", "Headers (JSON)")}
            </Label>
            <Textarea
              className="font-mono text-sm"
              id="webhookHeaders"
              placeholder='{"Authorization": "Bearer xxx"}'
              rows={4}
              value={webhookHeaders}
              onChange={(e) => setWebhookHeaders(e.target.value)}
            />
          </div>

          <Button
            className="w-full"
            disabled={updateMutation.isPending}
            onClick={handleSaveWebhook}
          >
            <Save className="mr-2 size-4" />
            {t("common.save", "Save")}
          </Button>
        </CardContent>
      </Card>
      {/* Error Report Recipient */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Bug className="size-5" />
            <CardTitle className="text-lg">
              {t("adminDashboard.alerts.errorReportChannel", "Error Reporting")}
            </CardTitle>
          </div>
          <CardDescription>
            {t(
              "adminDashboard.alerts.errorReportChannelDesc",
              "Email addresses where user-submitted error reports are sent"
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="errorReportRecipient">
              {t("adminDashboard.alerts.errorReportRecipient", "Recipients (one per line)")}
            </Label>
            <Textarea
              id="errorReportRecipient"
              placeholder="support@company.com&#10;ops@company.com"
              rows={4}
              value={errorReportRecipient}
              onChange={(e) => setErrorReportRecipient(e.target.value)}
            />
            <p className="text-muted-foreground text-xs">
              {t(
                "adminDashboard.alerts.errorReportRecipientDesc",
                "Users can report errors from the chat interface. Reports will be sent to all configured addresses."
              )}
            </p>
          </div>

          <Button
            className="w-full"
            disabled={updateMutation.isPending}
            onClick={handleSaveErrorReportRecipient}
          >
            <Save className="mr-2 size-4" />
            {t("common.save", "Save")}
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}
